import type jQuery from 'jquery';

import type { TypeRequestMethod } from './types';

import { merge } from '../../helper';

import exExternal from '../../external';

// ----------------------------------------------------------------------------

type TypeJQueryAjaxConfig = Parameters<typeof jQuery.ajax>[0];
type TypeJQueryAjaxRequest = ReturnType<typeof jQuery.ajax>;

export type TypeAjaxConfig = TypeJQueryAjaxConfig & {
    merge?: any;
    method?: TypeRequestMethod;
    url?: string;
    responseType?: string;
    upload?: boolean;
    fnSuccess?: (data: any, status: string, xhr: TypeJQueryAjaxRequest) => void;
    fnError?: (xhr: TypeJQueryAjaxRequest, status: string, error: any) => void;
    fnDone?: (data: any, status: string, xhr: TypeJQueryAjaxRequest) => void;
    fnFail?: (xhr: TypeJQueryAjaxRequest, status: string, error: any) => void;
};

// ----------------------------------------------------------------------------

function ajax(config: TypeAjaxConfig = {}): TypeJQueryAjaxRequest {
    // Default Config
    const defConfig: TypeAjaxConfig = {
        method: 'GET',
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const ajaxConfig: TypeAjaxConfig = {
        ...defConfig,
    };

    // ------------------------------------------------------------------------

    // If there is no url
    if (!defConfig.url) {
        throw new Error('Missing "url", is required.');
    }

    // ------------------------------------------------------------------------

    // If using response type array buffer
    if (defConfig.responseType === 'arraybuffer') {
        ajaxConfig.dataType = 'binary';
        ajaxConfig.xhrFields = {
            responseType: 'arraybuffer',
        };
    }

    // If using upload
    if (defConfig.upload === true) {
        ajaxConfig.async = true;
        ajaxConfig.cache = false;
        ajaxConfig.contentType = false;
        ajaxConfig.processData = false;
    }

    // ------------------------------------------------------------------------

    // If using function success
    if (typeof defConfig.fnSuccess === 'function') {
        /*
         * Success Params
         * (data, textStatus, jqXHR)
         */

        // Create event (success)
        ajaxConfig.success = function (data: any, status: string, xhr: TypeJQueryAjaxRequest): void {
            defConfig.fnSuccess?.(data, status, xhr);
        };
    }

    // If using function error
    if (typeof defConfig.fnError === 'function') {
        /*
         * Error Params
         * (jqXHR, textStatus, errorThrown)
         */

        // Create event (error)
        ajaxConfig.error = function (xhr: TypeJQueryAjaxRequest, status: string, error: any): void {
            defConfig.fnError?.(xhr, status, error);
        };
    }

    // ------------------------------------------------------------------------

    // Ajax
    const ajaxRequest = exExternal.jquery.ajax(ajaxConfig);

    // ------------------------------------------------------------------------

    // If using function done
    if (typeof defConfig.fnDone === 'function') {
        /*
         * Done Params
         * (data, textStatus, jqXHR)
         */

        // Create event (done)
        void ajaxRequest.done(function (data: any, status: string, xhr: TypeJQueryAjaxRequest): any {
            defConfig.fnDone?.(data, status, xhr);
        });
    }

    // If using function fail
    if (typeof defConfig.fnFail === 'function') {
        /*
         * Fail Params
         * (jqXHR, textStatus, errorThrown)
         */

        // Create event (fail)
        void ajaxRequest.fail(function (xhr: TypeJQueryAjaxRequest, status: string, error: any): any {
            defConfig.fnFail?.(xhr, status, error);
        });
    }

    // ------------------------------------------------------------------------

    // Return
    return ajaxRequest;
}

// ----------------------------------------------------------------------------

export { ajax };
